import React, { ReactNode } from "react";
import { PageContainer } from "./PageContainer";
import { H1, H5, P } from "#common/headings";
import {
    FilterModal,
    FilterModalState,
    MteColumnEventWithTable,
    MultiTableComparator,
} from "@inode/multi-table-explorer";
import {
    TableContext as Context,
    TableContextResponse,
} from "#services/pilotService/types";
import {
    Card,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import { Tabs, TabType } from "#common/Tabs";
import {
    selectInterpretationsThatAreFavorited,
    selectMappedInterpretationTables,
} from "#selectors";
import { useAppDispatch, useAppSelector } from "#/store";
import * as TableContext from "#store/tablecontext";
import { cloneDeep, findIndex, isEqual, uniq } from "lodash";
import { ErrorBoundary } from "#common/ErrorBoundary";
import {
    alignColumnAttributes,
    clusterRedundancyMatrix,
    CollapseableColumn,
    computeColumnAttributes,
    getColumnSortOrder,
    mergeColumnAttributes,
    mergeRedundancyMatrices,
} from "util/columnSorting";
import {
    InodeTableInformation,
    InodeTableInformationProps,
} from "#components/InodeTableInformation";
import { RadarContainer } from "#pages/results/RadarContainer";
import { Routes } from "#/config";
import {
    generateCellContextMenu,
    generateColumnContextMenu,
    generateRowContextMenu,
    generateTableContextMenu,
} from "#components/Model/mteHelpers";
import { DoiConfiguration } from "../Model/Doi/DoiConfiguration";
import * as Logging from "#store/logging";

const PREFERRED_MAX_VISIBLE_COLUMN_COUNT = 8;
const sortingModes = [
    "Alphanumeric Ascending",
    "Alphanumeric Descending",
    "Degree Of Interest",
];

/**
 * Finds the TableContext corresponding to the given list of tableIds
 * @param tableIds
 * @param contextArray
 * @returns The TableContext or undefined if no corresponding context could be found.
 */
function getTableContext(
    tableIds: number[],
    contextArray: TableContextResponse[]
): Context | undefined {
    for (let i = 0; i < contextArray.length; ++i) {
        const contextTables = contextArray[i].tableIds;
        // check if length of tableId Array matches
        if (contextTables.length !== tableIds.length) continue;
        // check if contents of tableId Array match (order is irrelevant)
        if (
            contextTables.every(tableId => tableIds.includes(tableId)) &&
            tableIds.every(tableId => contextTables.includes(tableId))
        ) {
            return contextArray[i].context;
        }
    }
    return undefined;
}

function mteRender(p: InodeTableInformationProps): ReactNode {
    return <InodeTableInformation {...p} />;
}

export const DataLibraryPage = () => {
    const collapsible = true;
    const categoricalMax = 5;

    const dispatch = useAppDispatch();

    // component state
    const query = useAppSelector(state => state.uiState.query);

    const [sortingMode, setSortingMode] = React.useState<string>(
        sortingModes[2]
    );
    const [columnOrder, setColumnOrder] = React.useState<CollapseableColumn[]>(
        []
    );

    const [columnSelection, setColumnSelection] = React.useState<string[]>([]);

    // store access
    const interpretations = useAppSelector(
        selectInterpretationsThatAreFavorited
    );
    const mappedTables = useAppSelector(state =>
        selectMappedInterpretationTables(state, interpretations)
    );
    // useWhatChanged([mappedTables], "mappedTables");

    const tc = useAppSelector(state => {
        return state.tableContext.contexts;
    });
    const doiWeights = useAppSelector(state => {
        return state.uiState.doiWeights;
    });
    const favoriteColumns = useAppSelector(state => {
        return state.uiState.favoriteColumns;
    });
    const [filterModalState, setFilterModalState] =
        React.useState<FilterModalState>({
            open: false,
            columnName: "",
        });

    // compute context of the interpretations within the cart
    React.useEffect(() => {
        const tableIds = uniq(interpretations.map(x => x.table));
        dispatch(TableContext.tableContextActions(tableIds));
    }, [interpretations]);

    // compute clustering of table columns
    const [mergedMatrix, clustering] = React.useMemo(() => {
        const matrix = mergeRedundancyMatrices(mappedTables.map(x => x.table));
        const clustering = clusterRedundancyMatrix(matrix.matrix, 0.2, 2);
        return [matrix, clustering];
    }, [mappedTables]);

    // compute column DOI Attributes
    const columnAttributes = React.useMemo(() => {
        const cartTables = interpretations.map(x => x.table);
        const cartContext = getTableContext(cartTables, tc);
        return computeColumnAttributes(
            mappedTables.map(x => x.table),
            cartContext,
            query,
            favoriteColumns
        );
    }, [mappedTables, interpretations, tc, favoriteColumns, query]);

    const mergedColumnAttributes = React.useMemo(
        () => mergeColumnAttributes(columnAttributes),
        [columnAttributes]
    );
    // useWhatChanged([clustering, mergedColumnAttributes, doiWeights, mergedMatrix],'clustering, mergedColumnAttributes, doiWeights, mergedMatrix', 'anysuffix-string'); // debugs the below useEffect

    // compute default column ordering, invariant of user clicks.
    React.useEffect(() => {
        const alignedAttributes = alignColumnAttributes(
            mergedColumnAttributes,
            mergedMatrix.columnNames
        );
        const newColumnOrder = getColumnSortOrder(
            alignedAttributes,
            doiWeights,
            clustering,
            undefined,
            PREFERRED_MAX_VISIBLE_COLUMN_COUNT,
            true
        );
        setColumnOrder(oldOrder => {
            if (!isEqual(oldOrder, newColumnOrder)) {
                return newColumnOrder;
            } else {
                return oldOrder;
            }
        });
    }, [clustering, mergedColumnAttributes, doiWeights, mergedMatrix]);

    function handleCollapse(column: string, collapseCluster: boolean) {
        const toCollapse: string[] = [];
        if (collapseCluster) {
            // find column in unsorted order
            const foundIndex = findIndex(
                mergedColumnAttributes,
                d => d.name === column
            );
            if (foundIndex !== -1) {
                const clusterId = clustering[foundIndex];
                // collapse all columns within the same cluster
                toCollapse.push(
                    ...clustering
                        .map((d, i) => [d, i])
                        .filter(d => d[0] === clusterId)
                        .map(d => mergedColumnAttributes[d[1]].name)
                );
            }
        } else {
            toCollapse.push(column);
        }

        // find column indices
        const columnIndices: number[] = [];
        for (const c of toCollapse) {
            const foundIndex = findIndex(columnOrder, d => d.name === c);
            if (foundIndex !== -1) {
                columnIndices.push(foundIndex);
            }
        }
        // remove lowest index (do not collapse representative of a cluster)
        if (columnIndices.length > 1) {
            const minIndex = Math.min(...columnIndices);
            columnIndices.splice(columnIndices.indexOf(minIndex), 1);
        }
        // update columnOrder
        if (columnIndices.length > 0) {
            const newColOrder = cloneDeep(columnOrder);
            columnIndices.forEach(d => (newColOrder[d].collapseable = true));
            setColumnOrder(oldOrder => {
                if (!isEqual(oldOrder, newColOrder)) {
                    return newColOrder;
                } else {
                    return oldOrder;
                }
            });
        }
    }

    function handleColumnHover(columns: string[]) {
        setColumnSelection(columns);
    }

    const cellContextMenu = generateCellContextMenu(mappedTables, dispatch);
    const columnContextMenu = [
        {
            label: "Filter by values",
            action: (evt: MteColumnEventWithTable) => {
                dispatch(
                    Logging.log("click/by-value-context-menu-option", {
                        sql: mappedTables[evt.table].table.sourceQuery,
                        columns: [evt.column],
                    })
                );

                setFilterModalState({
                    open: true,
                    columnName: evt.column,
                    table: mappedTables[evt.table].table,
                });
            },
        },
        ...generateColumnContextMenu(mappedTables, dispatch, handleCollapse),
    ];
    const rowContextMenu = generateRowContextMenu(mappedTables, dispatch);
    const tableContextMenu = generateTableContextMenu(
        dispatch,
        interpretations,
        mappedTables
    );

    const navigationTabs: TabType[] = [
        {
            label: "View Preferences",
            content: (
                <>
                    <H5 sx={{ marginBottom: "10px" }}>Column ordering</H5>
                    <FormControl fullWidth>
                        <InputLabel id="ordering-select-label">
                            Sorting Mode
                        </InputLabel>
                        <Select
                            labelId="ordering-select-label"
                            label="Sorting Mode"
                            id="ordering-select"
                            value={sortingMode}
                            onChange={evt => setSortingMode(evt.target.value)}
                        >
                            {sortingModes.map(d => (
                                <MenuItem key={"sortingMode_" + d} value={d}>
                                    {d}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <DoiConfiguration
                        columnOrder={columnOrder}
                        allAttributes={columnAttributes}
                        columnHoverCallback={handleColumnHover}
                        mapSpacing={0}
                        disabled={sortingMode !== sortingModes[2]}
                    />
                </>
            ),
        },
        {
            label: "Radar",
            content: (
                <>
                    <P>Get an idea how the tables relate to each other</P>
                    <ErrorBoundary>
                        <RadarContainer interpretations={interpretations} />
                    </ErrorBoundary>
                </>
            ),
        },
    ];
    const mteTables = React.useMemo(
        () => mappedTables.map(x => x.table),
        [mappedTables]
    );
    const collapsedCols = React.useMemo(() => {
        if (sortingMode === sortingModes[2]) {
            return columnOrder.filter(x => x.collapseable).map(x => x.name);
        } else {
            return [];
        }
    }, [columnOrder, sortingMode]);
    const columnOrderMemo = React.useMemo(() => {
        if (sortingMode === sortingModes[2]) {
            return columnOrder.map(x => x.name);
        } else if (sortingMode === sortingModes[1]) {
            return columnOrder
                .map(x => x.name)
                .sort()
                .reverse();
        } else {
            return columnOrder.map(x => x.name).sort();
        }
    }, [columnOrder, sortingMode]);
    const onCollapsedColumnHeaderClick = (cols: string[]) => {
        const newColOrder = cloneDeep(columnOrder);
        for (let i = 0; i < cols.length; ++i) {
            const foundIndex = findIndex(newColOrder, d => d.name === cols[i]);
            if (foundIndex !== -1) {
                newColOrder[foundIndex].collapseable = false;
            }
        }
        if (!isEqual(columnOrder, newColOrder)) {
            setColumnOrder(newColOrder);
        }
    };

    return (
        <PageContainer maxWidth={false}>
            <H1>{Routes.dataLibrary.label}</H1>
            <Typography variant={"body2"}>
                Comparing {mappedTables.length} tables (of{" "}
                {interpretations.length} interpretations) with a total of{" "}
                {columnOrder.length} columns
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={10}>
                    <ErrorBoundary>
                        <FilterModal
                            state={filterModalState}
                            handleClose={() =>
                                setFilterModalState({
                                    ...filterModalState,
                                    open: false,
                                })
                            }
                            handleFilter={value => {
                                console.log(
                                    "@hatieke: Implement your crazy logic here :)"
                                );
                                console.group();
                                console.log(
                                    "Remember: Value is dependent on column type and may be null, too:)"
                                );
                                console.log(value);
                                console.log(filterModalState.columnName);
                                console.log(filterModalState.table);
                                console.groupEnd();

                                setFilterModalState({
                                    ...filterModalState,
                                    open: false,
                                });
                            }}
                        />
                    </ErrorBoundary>
                    <ErrorBoundary>
                        <MultiTableComparator
                            tables={mteTables}
                            dataExpandable={collapsible}
                            categoricalMax={categoricalMax}
                            cellContextMenu={cellContextMenu}
                            columnContextMenu={columnContextMenu}
                            rowContextMenu={rowContextMenu}
                            tableContextMenu={tableContextMenu}
                            selection={columnSelection}
                            wrapInTableContainer={true}
                            hideColumnTitle={true}
                            hideTableInformation={false}
                            render={mteRender}
                            maxContentCols={9999}
                            columnOrder={columnOrderMemo}
                            collapsedCols={collapsedCols}
                            onCollapsedColumnHeaderClick={
                                onCollapsedColumnHeaderClick
                            }
                            // disableMouseOverMenu
                        />
                    </ErrorBoundary>
                </Grid>
                <Grid item xs={2}>
                    <Card
                        sx={{
                            position: "sticky",
                            top: "5rem",
                        }}
                    >
                        <Tabs tabs={navigationTabs} defaultTab={0} />
                    </Card>
                </Grid>
                {/*<Grid item xs={4}>*/}
                {/*    <H4>Table ranking (DOI)</H4>*/}
                {/*    <Todo>What could be interesting to use as DOI?</Todo>*/}
                {/*</Grid>*/}
            </Grid>
        </PageContainer>
    );
};
